import { ChatDetailsResponse } from '@choco-admin/types';
import { Avatar, Chip, Button } from '@heroui/react';
import React, { useState } from 'react';
import { FiUsers, FiMail, FiPhone, FiX, FiPlus } from 'react-icons/fi';

import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useChatUserOperations } from '../../../../../hooks';
import { useChatPageContext } from '../../../contexts';

import { AddBuyerUserModal } from './AddBuyerUserModal';

interface BuyerUsersSectionProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const BuyerUsersSection: React.FC<BuyerUsersSectionProps> = ({
  chatData,
  chatId,
}) => {
  const { chatData: contextChatData, updateChatData } = useChatPageContext();
  const currentChatData = contextChatData || chatData;
  const buyerUsers = currentChatData.users.filter((user) => !user.supplier);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { addNotification } = useNotification();
  const { removeUsers, isLoading } = useChatUserOperations({
    onSuccess: () => {
      addNotification({
        title: 'User Removed',
        message: 'User has been successfully removed from the chat.',
        type: 'success',
      });
      setRemovingUserId(null);
    },
    onError: (error) => {
      addNotification({
        title: 'Remove Failed',
        message: error.message || 'Failed to remove user from chat.',
        type: 'error',
      });
      setRemovingUserId(null);
    },
  });

  const handleRemoveUser = async (userId: string) => {
    setRemovingUserId(userId);
    try {
      await removeUsers([
        { chatId, userId, userType: 'buyer', operation: 'remove' },
      ]);

      // Update local state to remove the user immediately
      updateChatData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          users: prevData.users.filter((user) => user.id !== userId),
        };
      });
    } catch (error) {
      // Error is handled by the hook's onError callback
    }
  };

  return (
    <div className="space-y-4 ml-9">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FiUsers className="w-5 h-5 text-primary" />
          <div>
            <h4 className="text-lg font-medium text-foreground">Buyer Users</h4>
            <p className="text-sm text-foreground-500">
              {buyerUsers.length} user{buyerUsers.length !== 1 ? 's' : ''} with
              access to this chat
            </p>
          </div>
        </div>
        <Button
          size="sm"
          color="primary"
          variant="light"
          startContent={<FiPlus className="w-4 h-4" />}
          onPress={() => setIsAddModalOpen(true)}
        >
          Add User
        </Button>
      </div>

      {buyerUsers.length > 0 ? (
        <div className="space-y-3">
          {buyerUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between p-4 rounded-lg bg-content2 border border-divider"
            >
              <div className="flex items-center gap-3">
                <Avatar name={user.name} size="md" className="text-small" />
                <div>
                  <p className="font-medium text-foreground">{user.name}</p>
                  <div className="flex items-center gap-4 text-sm text-foreground-500 mt-1">
                    {user.email && (
                      <div className="flex items-center gap-1">
                        <FiMail className="w-4 h-4" />
                        {user.email}
                      </div>
                    )}
                    {user.phone && (
                      <div className="flex items-center gap-1">
                        <FiPhone className="w-4 h-4" />
                        {user.phone}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  {user.orderPreferences?.whatsApp && (
                    <Chip size="sm" variant="flat" color="success">
                      WhatsApp
                    </Chip>
                  )}
                  {user.orderPreferences?.sms && (
                    <Chip size="sm" variant="flat" color="primary">
                      SMS
                    </Chip>
                  )}
                  {user.orderPreferences?.fax && (
                    <Chip size="sm" variant="flat" color="secondary">
                      Fax
                    </Chip>
                  )}
                </div>
                <Button
                  isIconOnly
                  size="sm"
                  color="danger"
                  variant="light"
                  isLoading={removingUserId === user.id}
                  isDisabled={isLoading}
                  onPress={() => handleRemoveUser(user.id)}
                  aria-label={`Remove ${user.name} from chat`}
                >
                  <FiX className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 bg-content2 rounded-lg border border-divider">
          <p className="text-foreground-500 italic">No buyer users assigned</p>
        </div>
      )}

      {/* Add User Modal */}
      <AddBuyerUserModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        chatId={chatId}
        buyerId={currentChatData.buyerEntity?.id || ''}
      />
    </div>
  );
};
